import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Save } from 'lucide-react';
import DefaultLayout from '../../../layout/DefaultLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import PdfDesignService from '../../../services/pdfDesign/PdfDesignService';
import { PdfTemplateStyleCode, PdfTemplateStyleOptionDTO } from '../../../types/pdfDesign/pdfDesign';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import useAlerts from '../../../hooks/useAlerts';

const DOCUMENT_KIND = 'QUOTATION';

const QuotationPdfDesignPage: React.FC = () => {
  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();

  const [styles, setStyles] = useState<PdfTemplateStyleOptionDTO[]>([]);
  const [savedStyle, setSavedStyle] = useState<PdfTemplateStyleCode | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<PdfTemplateStyleCode | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const design = await PdfDesignService.getDesign(DOCUMENT_KIND);
        setStyles(design.availableStyles);
        setSavedStyle(design.selectedStyle);
        setSelectedStyle(design.selectedStyle);
      } catch (error) {
        handle(error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedStyle) return;

    let cancelled = false;

    (async () => {
      setPreviewLoading(true);
      try {
        const html = await PdfDesignService.previewQuotationTemplate(selectedStyle);
        if (!cancelled) setPreviewHtml(html);
      } catch (error) {
        if (!cancelled) handle(error);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStyle]);

  const handleSave = async () => {
    if (!selectedStyle) return;

    try {
      setSaving(true);
      await PdfDesignService.updateDesign(DOCUMENT_KIND, selectedStyle);
      setSavedStyle(selectedStyle);
      showAlert('success', 'Guardado', 'El diseño de las cotizaciones en PDF se actualizó correctamente.');
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = selectedStyle !== null && selectedStyle !== savedStyle;

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      {loading ? (
        <div className="flex items-center justify-center rounded-sm border border-stroke bg-white p-10 shadow-default dark:border-strokedark dark:bg-boxdark">
          <Loader2 size={20} className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-5">
          {/* 🔹 Selector de diseños */}
          <div className="flex flex-col gap-4 xl:col-span-2">
            <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
              <div className="flex items-center gap-2 border-b border-stroke px-5 py-3 dark:border-strokedark">
                <FileText size={16} className="text-primary" />
                <h3 className="text-sm font-medium text-black dark:text-white">
                  Diseño de PDF — Cotizaciones
                </h3>
              </div>

              <div className="flex flex-col gap-3 p-5">
                {styles.map((style) => {
                  const isSelected = selectedStyle === style.code;
                  const isSaved = savedStyle === style.code;

                  return (
                    <button
                      key={style.code}
                      type="button"
                      onClick={() => setSelectedStyle(style.code)}
                      className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow-sm'
                          : 'border-stroke hover:border-primary/40 hover:bg-primary/5 dark:border-strokedark'
                      }`}
                    >
                      <span>
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-black dark:text-white">
                            {style.label}
                          </span>
                          {isSaved && (
                            <span className="rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success">
                              Activo
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                          {style.description}
                        </span>
                      </span>

                      <span
                        className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${
                          isSelected ? 'border-primary bg-primary' : 'border-stroke dark:border-strokedark'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="flex h-[46px] items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-white transition-all duration-200 hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {hasChanges ? 'Guardar diseño' : 'Diseño guardado'}
            </button>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              La vista previa se actualiza al instante con una cotización de ejemplo. Los PDFs
              reales de cotizaciones usarán el diseño que quede marcado como <strong>Activo</strong>{' '}
              después de guardar.
            </p>
          </div>

          {/* 🔹 Vista previa en vivo */}
          <div className="relative overflow-hidden rounded-sm border border-stroke bg-gray-2 shadow-default dark:border-strokedark dark:bg-boxdark-2 xl:col-span-3">
            <div className="border-b border-stroke bg-white px-5 py-3 dark:border-strokedark dark:bg-boxdark">
              <h3 className="text-sm font-medium text-black dark:text-white">Vista previa</h3>
            </div>

            <div className="relative h-[720px]">
              {previewLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-boxdark-2/60">
                  <Loader2 size={24} className="animate-spin text-primary" />
                </div>
              )}

              {previewHtml ? (
                <iframe
                  title="Vista previa del diseño de cotización"
                  srcDoc={previewHtml}
                  className="h-full w-full border-0 bg-white"
                />
              ) : (
                !previewLoading && (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                    Selecciona un diseño para ver la vista previa.
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </DefaultLayout>
  );
};

export default QuotationPdfDesignPage;
